import { useEffectOnlyOnce } from '@base/utility/CustomHooks';
import { sendGetRequest } from '@vh/services/PDService';
import { useCallback, useRef, useState } from 'react';

const makeResponseRequest = (dc) => {
  if (!dc) return;
  if (dc.readyState !== 'open') {
    console.error(
      'Cannot make response request, readystate = ' + dc.readyState
    );
    return;
  }
  console.log('send response request');
  dc.send(
    JSON.stringify({
      type: 'response.create',
    })
  );
};

/**
 * @type {(options: {
 *  vhId: number;
 *  shareToken: string;
 * }) => {
 *  startSession: (audioRef: React.RefObject<HTMLAudioElement>, micStream: MediaStream, voice: string, userSelectedBCP47Code?: string) => Promise<void>;
 *  sendText: (text: string) => void;
 *  stopSession: () => void;
 *  connected: boolean;
 *  connecting: boolean;
 *  error: string | null;
 *  audioStream: MediaStream | null;
 *  setupEvents: (options: {
 *    onUserTranscriptComplete?: (id: string, transcript: string) => void,
 *    onUserSpeechStopped?: (id: string) => void,
 *    onUserSpeechStarted?: (id: string) => void,
 *    onOpenAITranscriptDone?: (id: string, transcript: string) => void,
 *    onOpenAITranscriptPartAdded?: (id: string, transcriptPart: string) => void,
 *    onOpenAITranscriptStarted?: (id: string) => void,
 *    onUserTextRequestComplete?: (id: string, text: string) => void;
 *  }) => void;
 * }}
 * */
export const useRealTimeOpenAI = ({ vhId, shareToken }) => {
  /** @type {React.MutableRefObject<RTCPeerConnection | null>} */
  const peerConnectionRef = useRef(null);
  /** @type {React.MutableRefObject<RTCDataChannel | null>} */
  const dataChannelRef = useRef(null);
  const [ephemeralKey, setEphemeralKey] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [audioStream, setAudioStream] = useState(null);

  /** @type {(text: string) => void} */
  const sendText = useCallback((text) => {
    if (!dataChannelRef.current) return;
    if (dataChannelRef.current.readyState !== 'open') {
      console.error(
        'Cannot send text, readystate = ' + dataChannelRef.current.readyState
      );
      return;
    }
    console.log('send text');
    console.log(dataChannelRef.current);
    dataChannelRef.current.send(
      JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [
            {
              type: 'input_text',
              text,
            },
          ],
        },
      })
    );
  }, []);

  /**
   * @type {(audioRef: React.MutableRefObject<HTMLAudioElement | null>, micStream: MediaStream, voice: string) => Promise<void>}
   * */
  const startSession = useCallback(
    async (audioRef, micStream, voice, userSelectedBCP47Code) => {
      const tokenResponse = await sendGetRequest(
        `virtual-humans/${vhId}/openai-realtime/session?voice=${voice}${
          userSelectedBCP47Code
            ? `&userSelectedBCP47Code=${userSelectedBCP47Code}`
            : ''
        }`,
        shareToken
      );
      setEphemeralKey(tokenResponse.data.client_secret.value);

      // Create a peer connection
      const pc = new RTCPeerConnection();
      pc.addEventListener(
        'connectionstatechange',
        (event) => {
          console.log('connection state change');
          console.log(pc.connectionState);
          switch (pc.connectionState) {
            case 'new':
            case 'connecting':
              setConnecting(true);
              setError(null);
              break;
            case 'connected':
              setConnected(true);
              setConnecting(false);
              setError(null);
              break;
            case 'disconnected':
            case 'closed':
              setConnected(false);
              setConnecting(false);
              setError('Disconnected');
              setAudioStream(null);
              break;
            case 'failed':
              setConnected(false);
              setConnecting(false);
              setAudioStream(null);
              setError('WebRTC connection failed');
              break;
            default:
              break;
          }
        },
        false
      );

      // Set up to play remote audio from the model
      audioRef.current.autoplay = true;

      pc.ontrack = (e) => {
        audioRef.current.srcObject = e.streams[0];
        setAudioStream(e.streams[0]);
      };

      pc.addTrack(micStream.getTracks()[0]);

      // Set up data channel for sending and receiving events
      const dc = pc.createDataChannel('oai-events');

      dataChannelRef.current = dc;
      dataChannelRef.current.onerror = (event) => {
        console.error('Data channel error:', event);
      };

      dataChannelRef.current.onclose = () => {
        console.warn('Data channel closed unexpectedly');
      };
      dataChannelRef.current.onmessage = (event) => {
        console.log('Received message:', event.data);
      };

      dataChannelRef.current.onopen = () => {
        console.log('Data channel is open');
      };

      dc.addEventListener('close', () => {
        setConnected(false);
        setConnecting(false);
        setError('Data connection failed');
      });

      // Start the session using the Session Description Protocol (SDP)
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const baseUrl = 'https://api.openai.com/v1/realtime';
      const sdpResponse = await fetch(`${baseUrl}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${tokenResponse.data.client_secret.value}`,
          'Content-Type': 'application/sdp',
        },
      });
      const answer = {
        type: /** @type {RTCSdpType} */ ('answer'),
        sdp: await sdpResponse.text(),
      };

      await pc.setRemoteDescription(answer);

      peerConnectionRef.current = pc;
    },
    [shareToken, vhId]
  );

  const setupEvents = useCallback(
    ({
      onOpenAITranscriptDone,
      onOpenAITranscriptPartAdded,
      onOpenAITranscriptStarted,
      onUserSpeechStarted,
      onUserSpeechStopped,
      onUserTranscriptComplete,
      onUserTextRequestComplete,
    }) => {
      if (!dataChannelRef.current) {
        console.error('No datachannel setup yet');
        return;
      }
      dataChannelRef.current.addEventListener('message', (e) => {
        // Realtime server events appear here!
        console.log('realtime server event');
        const data = JSON.parse(e.data);
        console.log(data);
        switch (data.type) {
          case 'conversation.item.created':
            // data.item.content.transcript
            console.log('xxxxxxxxxxxxxx');
            console.log(data);
            // if we just submitted text then we need to ask for a response
            if (
              data.item?.role === 'user' &&
              data.item?.content?.[0]?.type === 'input_text'
            ) {
              onUserTextRequestComplete(
                data.item_id,
                data.item?.content?.[0]?.text
              );
              makeResponseRequest(dataChannelRef.current);
            }
            break;
          case 'conversation.item.input_audio_transcription.completed':
            if (onUserTranscriptComplete) {
              onUserTranscriptComplete(data.item_id, data.transcript);
            }
            break;
          case 'input_audio_buffer.speech_started':
            if (onUserSpeechStarted) {
              onUserSpeechStarted(data.item_id);
            }
            break;
          case 'input_audio_buffer.speech_stopped':
            if (onUserSpeechStopped) {
              onUserSpeechStopped(data.item_id);
            }
            break;
          case 'response.done':
            console.log('ooooooooooooooooooooooooo');
            console.log(data);
            break;
          case 'response.created':
            break;
          case 'response.output_item.added':
            if (onOpenAITranscriptStarted) {
              onOpenAITranscriptStarted(data.item.id);
            }
            break;
          case 'response.output_item.done':
            // data.item.content[0].text
            break;
          // case 'response.content_part.added':
          //   if (data.part.text) {
          //     dispatch(addOngoingBotTextPart(data.part.text));
          //   }
          //   break;
          // case 'response.content_part.done':
          //   dispatch(setOngoingBotText(data.part.text));
          //   break;
          case 'response.text.done':
            // if(onOpenAISpeechStopped){
            //   onOpenAISpeechStopped()
            // }
            // dispatch(setFinalBotText(data.text));
            break;
          case 'response.audio_transcript.delta':
            if (onOpenAITranscriptPartAdded) {
              onOpenAITranscriptPartAdded(data.item_id, data.delta);
            }
            break;
          case 'response.audio_transcript.done':
            if (onOpenAITranscriptDone) {
              onOpenAITranscriptDone(data.item_id, data.transcript);
            }
            break;
          case 'response.audio.done':
            break;
          default:
            break;
        }
      });
    },
    []
  );

  useEffectOnlyOnce(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
  });

  const stopSession = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
      dataChannelRef.current = null;
    }
  }, []);

  return {
    startSession,
    stopSession,
    setupEvents,
    sendText,
    connected,
    connecting,
    error,
    audioStream,
  };
};
